/**
 * Snowcubes Reviews Core
 * - Provider-neutral storefront normalization + cache helpers.
 * - Private verification evidence belongs outside the theme repo.
 */
(function initSnowcubesReviewsCore(global) {
  'use strict';

  var API_SCHEMA = 'snowcubes.ownedReviews.v1';
  var REVIEWS_UI_FLAG_KEY = 'sc_reviews_ui';
  var DEBUG_FLAG_KEY = 'sc_debug_reviews';
  var CACHE_PREFIX = 'sc_reviews';
  var DEFAULT_API_BASE = '/apps/snowcubes-reviews';
  var DEFAULT_PER_PAGE = 10;
  var DEFAULT_TTL_MS = 5 * 60 * 1000;
  var STAR_MAX = 5;
  var DEFAULT_SORT = 'recent';
  var DEFAULT_FILTER = 'all';

  var SORT_VALUES = {
    recent: true,
    highest: true,
    lowest: true,
    photos: true,
  };

  var FILTER_VALUES = {
    all: true,
    verified: true,
    photos: true,
  };

  var VERIFICATION_KEYS = {
    verified_order: 'pages.reviews.verified_order',
    verified_reviewer: 'pages.reviews.verified_reviewer',
    gift_recipient: 'pages.reviews.gift_recipient',
  };

  function safeGetSearchParams() {
    try {
      return new URLSearchParams(global.location && global.location.search ? global.location.search : '');
    } catch (_) {
      return new URLSearchParams('');
    }
  }

  function safeGetStorageItem(storage, key) {
    try {
      return storage && storage.getItem ? storage.getItem(key) : null;
    } catch (_) {
      return null;
    }
  }

  function safeSetStorageItem(storage, key, value) {
    try {
      if (!storage || !storage.setItem) return false;
      storage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function safeRemoveStorageItem(storage, key) {
    try {
      if (!storage || !storage.removeItem) return false;
      storage.removeItem(key);
      return true;
    } catch (_) {
      return false;
    }
  }

  function safeJsonParse(raw) {
    try {
      return { ok: true, value: JSON.parse(raw) };
    } catch (error) {
      return { ok: false, error: error };
    }
  }

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function normalizeString(value, fallback) {
    if (value === null || value === undefined) return fallback || '';
    var str = String(value).trim();
    return str || fallback || '';
  }

  function translate(key, values) {
    var table = global.SnowcubesLocale || {};
    var value = table[key];
    if (typeof value !== 'string' || !value) return key;
    return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, function (_match, name) {
      return values && values[name] !== undefined ? String(values[name]) : '';
    });
  }

  function clampRating(value) {
    var num = Number(value);
    if (!Number.isFinite(num)) return 0;
    if (num < 1) return 0;
    if (num > STAR_MAX) return STAR_MAX;
    return Math.round(num * 10) / 10;
  }

  function normalizePositiveInt(value, fallback) {
    var num = Number(value);
    if (!Number.isFinite(num) || num < 1) return fallback;
    return Math.floor(num);
  }

  function normalizeReviewSort(value) {
    var sort = normalizeString(value, DEFAULT_SORT).toLowerCase().replace(/[^a-z_ -]+/g, '').replace(/[_ ]+/g, '-');
    if (sort === 'newest' || sort === 'newest-first' || sort === 'most-recent') return 'recent';
    if (sort === 'highest-rating' || sort === 'rating-desc') return 'highest';
    if (sort === 'lowest-rating' || sort === 'rating-asc') return 'lowest';
    if (sort === 'with-photos' || sort === 'photo') return 'photos';
    return SORT_VALUES[sort] ? sort : DEFAULT_SORT;
  }

  function normalizeReviewFilter(value) {
    var filter = normalizeString(value, DEFAULT_FILTER).toLowerCase().replace(/[^a-z_ -]+/g, '').replace(/[_ ]+/g, '-');
    if (filter === 'verified-order' || filter === 'verified-orders' || filter === 'verified-purchase') return 'verified';
    if (filter === 'with-photos' || filter === 'photo') return 'photos';
    return FILTER_VALUES[filter] ? filter : DEFAULT_FILTER;
  }

  function normalizeDate(value) {
    if (!value) return null;
    var date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  function isFlagEnabled(flagKey) {
    if (global.SnowcubesFlags && typeof global.SnowcubesFlags.isEnabled === 'function') {
      return global.SnowcubesFlags.isEnabled(flagKey);
    }

    var params = safeGetSearchParams();
    if (params.get(flagKey) === '1') return true;
    if (params.get(flagKey) === '0') return false;

    return safeGetStorageItem(global.localStorage, flagKey) === '1';
  }

  function isReviewsUIEnabled() {
    return isFlagEnabled(REVIEWS_UI_FLAG_KEY);
  }

  function isDebugEnabled() {
    return isFlagEnabled(DEBUG_FLAG_KEY);
  }

  function debug(event, data) {
    if (!isDebugEnabled()) return;
    if (data === undefined) {
      // eslint-disable-next-line no-console
      console.log('[Reviews] ' + event);
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[Reviews] ' + event, data);
  }

  function getVerificationLabel(status) {
    return VERIFICATION_KEYS[status] ? translate(VERIFICATION_KEYS[status]) : '';
  }

  function isVerifiedOrder(review) {
    return !!review && review.verificationStatus === 'verified_order';
  }

  function normalizePhoto(photo, index) {
    if (!photo) return null;
    var url = normalizeString(photo.url || photo.src || photo.imageUrl || photo.image_url);
    if (!url) return null;
    return {
      id: normalizeString(photo.id, 'photo_' + (index + 1)),
      url: url,
      alt: normalizeString(photo.alt || photo.altText || photo.caption, translate('pages.reviews.photo_alt')),
      width: normalizePositiveInt(photo.width, null),
      height: normalizePositiveInt(photo.height, null),
    };
  }

  function normalizeMerchantReply(input) {
    if (!isPlainObject(input)) return null;
    var body = normalizeString(input.body || input.content || input.reply || input.merchant_reply);
    if (!body) return null;
    return {
      body: body,
      createdAt: normalizeDate(input.createdAt || input.created_at || input.repliedAt || input.replied_at),
    };
  }

  function normalizeReview(raw) {
    var review = isPlainObject(raw) ? raw : {};
    var verificationStatus = normalizeString(
      review.verificationStatus || review.verification_status || review.status
    );
    var rating = clampRating(review.rating || review.score);
    var photos = Array.isArray(review.photos || review.images)
      ? (review.photos || review.images).map(normalizePhoto).filter(Boolean)
      : [];

    return {
      id: normalizeString(review.id || review.reviewId || review.review_id),
      rating: rating,
      title: normalizeString(review.title),
      body: normalizeString(review.body || review.content || review.review),
      reviewerName: normalizeString(
        review.reviewerName ||
          review.reviewer_name ||
          (review.reviewer && (review.reviewer.displayName || review.reviewer.name)),
        translate('pages.reviews.customer_name')
      ),
      createdAt: normalizeDate(review.createdAt || review.created_at || review.submittedAt || review.submitted_at),
      verificationStatus: verificationStatus,
      verificationLabel: getVerificationLabel(verificationStatus),
      incentivized: review.incentivized === true || review.isIncentivized === true,
      incentiveDescription: normalizeString(review.incentiveDescription || review.incentive_description),
      collectionMethod: normalizeString(review.collectionMethod || review.collection_method),
      productHandle: normalizeString(review.productHandle || review.product_handle),
      merchantReply: normalizeMerchantReply(review.merchantReply || review.merchant_reply),
      photos: photos,
    };
  }

  function deriveSummary(raw, reviews) {
    var input = isPlainObject(raw && raw.summary) ? raw.summary : {};
    var reviewCount = Number(input.reviewCount || input.review_count);
    var averageRating = Number(input.averageRating || input.average_rating);

    if (!Number.isFinite(reviewCount)) reviewCount = reviews.length;
    if (!Number.isFinite(averageRating) && reviews.length > 0) {
      var sum = reviews.reduce(function (memo, review) {
        return memo + review.rating;
      }, 0);
      averageRating = sum / reviews.length;
    }

    if (!Number.isFinite(averageRating)) averageRating = 0;

    var verifiedCount = Number(input.verifiedCount || input.verified_count);
    if (!Number.isFinite(verifiedCount)) {
      verifiedCount = reviews.filter(isVerifiedOrder).length;
    }

    var photoCount = Number(input.photoCount || input.photo_count);
    if (!Number.isFinite(photoCount)) {
      photoCount = reviews.reduce(function (memo, review) {
        return memo + review.photos.length;
      }, 0);
    }

    return {
      averageRating: Math.round(clampRating(averageRating) * 10) / 10,
      reviewCount: Math.max(0, Math.floor(reviewCount)),
      verifiedCount: Math.max(0, Math.floor(verifiedCount)),
      photoCount: Math.max(0, Math.floor(photoCount)),
    };
  }

  function normalizeApiResponse(raw) {
    var input = isPlainObject(raw) ? raw : {};
    var reviews = Array.isArray(input.reviews) ? input.reviews.map(normalizeReview) : [];
    var summary = deriveSummary(input, reviews);
    var paginationInput = isPlainObject(input.pagination) ? input.pagination : {};

    return {
      schema: input.schema || API_SCHEMA,
      summary: summary,
      reviews: reviews,
      pagination: {
        page: normalizePositiveInt(paginationInput.page, 1),
        perPage: normalizePositiveInt(paginationInput.perPage || paginationInput.per_page, DEFAULT_PER_PAGE),
        hasMore: paginationInput.hasMore === true || paginationInput.has_more === true,
      },
    };
  }

  function getCacheKey(options) {
    var productId = normalizeString(options && options.productId, 'unknown');
    var handle = normalizeString(options && options.handle, 'unknown');
    var page = normalizePositiveInt(options && options.page, 1);
    var perPage = normalizePositiveInt(options && options.perPage, DEFAULT_PER_PAGE);
    var sort = normalizeReviewSort(options && options.sort);
    var filter = normalizeReviewFilter(options && options.filter);
    var targetReviewId = normalizeString(options && options.targetReviewId);
    var parts = [CACHE_PREFIX, 'list', productId, handle, 'p' + page, 'pp' + perPage, 'sort', sort, 'filter', filter];
    if (targetReviewId) parts.push('target', targetReviewId);
    return parts.join(':');
  }

  function getCached(key, now) {
    var raw = safeGetStorageItem(global.sessionStorage, key);
    if (!raw) return null;
    var parsed = safeJsonParse(raw);
    if (!parsed.ok || !isPlainObject(parsed.value)) {
      safeRemoveStorageItem(global.sessionStorage, key);
      return null;
    }
    var expiresAt = Number(parsed.value.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= (now || Date.now())) {
      safeRemoveStorageItem(global.sessionStorage, key);
      return null;
    }
    return parsed.value.value;
  }

  function setCached(key, value, ttlMs, now) {
    var expiresAt = (now || Date.now()) + normalizePositiveInt(ttlMs, DEFAULT_TTL_MS);
    return safeSetStorageItem(
      global.sessionStorage,
      key,
      JSON.stringify({
        expiresAt: expiresAt,
        value: value,
      })
    );
  }

  function clearCache() {
    try {
      var storage = global.sessionStorage;
      if (!storage || !storage.length) return 0;
      var keys = [];
      for (var index = 0; index < storage.length; index += 1) {
        var key = storage.key(index);
        if (key && key.indexOf(CACHE_PREFIX + ':') === 0) keys.push(key);
      }
      keys.forEach(function (key) {
        storage.removeItem(key);
      });
      return keys.length;
    } catch (_) {
      return 0;
    }
  }

  function buildListUrl(options) {
    var opts = options || {};
    var base = normalizeString(opts.apiBase, DEFAULT_API_BASE);
    var origin = normalizeString(global.location && global.location.origin, 'https://trysnowcubes.com');
    var url = new URL(base, origin);
    url.searchParams.set('productId', normalizeString(opts.productId));
    url.searchParams.set('handle', normalizeString(opts.handle));
    url.searchParams.set('page', String(normalizePositiveInt(opts.page, 1)));
    url.searchParams.set('perPage', String(normalizePositiveInt(opts.perPage, DEFAULT_PER_PAGE)));
    url.searchParams.set('sort', normalizeReviewSort(opts.sort));
    url.searchParams.set('filter', normalizeReviewFilter(opts.filter));
    var targetReviewId = normalizeString(opts.targetReviewId);
    if (targetReviewId) url.searchParams.set('targetReviewId', targetReviewId);
    return url.toString();
  }

  function readMockJson(mockJsonId) {
    if (!mockJsonId || !global.document) return null;
    var node = global.document.getElementById(mockJsonId);
    if (!node) return null;
    var parsed = safeJsonParse(node.textContent || '');
    return parsed.ok ? parsed.value : null;
  }

  function fetchReviews(options) {
    var opts = options || {};
    var mock = readMockJson(opts.mockJsonId);
    if (mock) return Promise.resolve(normalizeApiResponse(mock));

    var cacheKey = getCacheKey(opts);
    var cached = getCached(cacheKey);
    if (cached) return Promise.resolve(normalizeApiResponse(cached));

    var fetchImpl = opts.fetch || global.fetch;
    if (typeof fetchImpl !== 'function') {
      return Promise.reject(new Error('fetch_unavailable'));
    }

    return fetchImpl(buildListUrl(opts), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(function (response) {
      if (!response || !response.ok) {
        throw new Error('reviews_fetch_failed');
      }
      return response.json();
    }).then(function (json) {
      var normalized = normalizeApiResponse(json);
      setCached(cacheKey, normalized, opts.ttlMs || DEFAULT_TTL_MS);
      return normalized;
    });
  }

  // JSON-LD aggregateRating helpers live in reviews-jsonld.js. In the browser
  // that file loads first and registers them on global.SnowcubesReviews; under
  // Node/jest we require it here. Either way we re-export them below so callers
  // resolving SnowcubesReviews.upsertAggregateJsonLd keep working unchanged.
  var jsonLd = global.SnowcubesReviews || {};
  if (typeof module !== 'undefined' && module.exports) {
    try {
      jsonLd = require('./reviews-jsonld.js');
    } catch (_) {
      // Fall back to whatever the browser global already carries.
    }
  }
  var buildAggregateRating = jsonLd.buildAggregateRating;
  var upsertAggregateJsonLd = jsonLd.upsertAggregateJsonLd;

  var api = {
    API_SCHEMA: API_SCHEMA,
    REVIEWS_UI_FLAG_KEY: REVIEWS_UI_FLAG_KEY,
    DEBUG_FLAG_KEY: DEBUG_FLAG_KEY,
    DEFAULT_API_BASE: DEFAULT_API_BASE,
    DEFAULT_PER_PAGE: DEFAULT_PER_PAGE,
    DEFAULT_TTL_MS: DEFAULT_TTL_MS,
    DEFAULT_SORT: DEFAULT_SORT,
    DEFAULT_FILTER: DEFAULT_FILTER,

    isReviewsUIEnabled: isReviewsUIEnabled,
    isDebugEnabled: isDebugEnabled,
    debug: debug,
    normalizeReview: normalizeReview,
    normalizeApiResponse: normalizeApiResponse,
    normalizeReviewSort: normalizeReviewSort,
    normalizeReviewFilter: normalizeReviewFilter,
    isVerifiedOrder: isVerifiedOrder,
    getVerificationLabel: getVerificationLabel,
    getCacheKey: getCacheKey,
    getCached: getCached,
    setCached: setCached,
    clearCache: clearCache,
    buildListUrl: buildListUrl,
    fetchReviews: fetchReviews,
    buildAggregateRating: buildAggregateRating,
    upsertAggregateJsonLd: upsertAggregateJsonLd,
  };

  global.SnowcubesReviews = global.SnowcubesReviews || {};
  Object.assign(global.SnowcubesReviews, api);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
